import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useInstructorMode } from '../hooks/useInstructorMode';
import { formatShortDate } from '../utils/dateUtils';
import './Search.css';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { buildUrl, isInstructorMode } = useInstructorMode();
  
  const queryFromUrl = searchParams.get('q') || '';
  
  const [query, setQuery] = useState(queryFromUrl);
  const [results, setResults] = useState({ songs: [], artists: [], playlists: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);

  const performSearch = useCallback(async (searchQuery) => {
    if (!searchQuery || searchQuery.trim().length === 0) {
      setResults({ songs: [], artists: [], playlists: [] });
      setHasSearched(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const url = `${API_BASE}/search?q=${encodeURIComponent(searchQuery)}${isInstructorMode ? '&show=all' : ''}`;
      const response = await axios.get(url);
      
      setResults(response.data);
      setHasSearched(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Search failed');
      console.error('Error searching:', err);
    } finally {
      setLoading(false);
    }
  }, [isInstructorMode]);

  useEffect(() => {
    if (queryFromUrl) {
      performSearch(queryFromUrl);
    }
  }, [queryFromUrl, performSearch]);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (query.trim()) {
      // Update URL with search query
      setSearchParams(prev => {
        prev.set('q', query.trim());
        if (isInstructorMode) {
          prev.set('instructor', '');
        }
        return prev;
      });
    } else {
      // Clear search
      setSearchParams(prev => {
        prev.delete('q');
        if (isInstructorMode) {
          prev.set('instructor', '');
        }
        return prev;
      });
    }
  };

  const getArtistLinks = (artists) => {
    if (!artists || artists.length === 0) return 'Unknown';
    
    return artists.map((artist, index) => (
      <span key={artist.id}>
        <a 
          href={buildUrl(`/artist/${artist.id}`)}
          className="artist-link"
        >
          {artist.name}
        </a>
        {index < artists.length - 1 && ', '}
      </span>
    ));
  };

  // Check if query is a BPM search (contains only numbers or numbers followed by "bpm")
  const isBpmQuery = queryFromUrl && /^\d+(\s*bpm)?$/i.test(queryFromUrl.trim());

  const totalResults = results.songs.length + results.artists.length + results.playlists.length;

  return (
    <div className="search-page">
      <div className="search-header">
        <h1>🔍 Search</h1>
        <p className="subtitle">Search for songs, artists, or playlists</p>
      </div>

      <form onSubmit={handleSubmit} className="search-form">
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Song name, artist, BPM (e.g., 70), or playlist date..."
          className="search-input"
          autoFocus
        />
        <button type="submit" className="search-button">
          Search
        </button>
      </form>

      {loading && (
        <div className="search-loading">
          <div className="spinner"></div>
          <p>Searching...</p>
        </div>
      )}

      {error && (
        <div className="search-error">
          <p>{error}</p>
        </div>
      )}

      {!loading && hasSearched && totalResults === 0 && (
        <div className="no-results">
          <p>No results found for "{queryFromUrl}"</p>
          <p className="no-results-hint">
            Try searching for:
            <br />• Song names (e.g., "Levitating")
            <br />• Artists (e.g., "Dua Lipa")
            <br />• BPM values (e.g., "70" or "70 BPM")
            <br />• Playlist dates (e.g., "1030" or "October 30")
          </p>
        </div>
      )}

      {!loading && hasSearched && totalResults > 0 && (
        <div className="search-results">
          {/* Songs Results - Use table format for BPM queries */}
          {results.songs.length > 0 && (
            <div className="results-section">
              <h2>Songs ({results.songs.length})</h2>
              {isBpmQuery ? (
                // Table format for BPM queries
                <div className="songs-table-container">
                  <table className="songs-table">
                    <thead>
                      <tr>
                        <th className="col-rank">#</th>
                        <th className="col-song">SONG</th>
                        {isInstructorMode && <th className="col-bpm">BPM</th>}
                        <th className="col-plays-last">PLAYS / LAST</th>
                      </tr>
                    </thead>
                    <tbody>
                      {results.songs.map((song, index) => (
                        <tr key={song.id}>
                          <td className="col-rank">{index + 1}</td>
                          <td className="col-song">
                            <div className="song-info">
                              <a 
                                href={buildUrl(`/song/${song.id}`)}
                                className="song-link"
                              >
                                {song.name}
                              </a>
                              <div className="artist-info">
                                {getArtistLinks(song.artists)}
                              </div>
                            </div>
                          </td>
                          {isInstructorMode && (
                            <td className="col-bpm instructor-bpm">
                              {song.bpm || '—'}
                            </td>
                          )}
                          <td className="col-plays-last">
                            <div className="plays-last-combined">
                              <div className="play-info">
                                <span className="icon">▶ </span>
                                <span className="plays-count">{song.total_plays || 1}</span>
                              </div>
                              {song.last_played_date && song.last_played_playlist_id ? (
                                <div className="date-info">
                                  <a href={buildUrl(`/playlist/${song.last_played_playlist_id}`)} className="playlist-link">
                                    {formatShortDate(song.last_played_date)}
                                  </a>
                                </div>
                              ) : song.last_played_date ? (
                                <div className="date-info">
                                  <span className="date-text">{formatShortDate(song.last_played_date)}</span>
                                </div>
                              ) : (
                                <div className="date-info">
                                  <span className="date-text">Never</span>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                // Card format for non-BPM queries
                <div className="results-grid">
                  {results.songs.map((song) => (
                    <a
                      key={song.id}
                      href={buildUrl(`/song/${song.id}`)}
                      className="result-card"
                    >
                      <div className="result-main">
                        <div className="result-title">{song.name}</div>
                        <div className="result-subtitle">
                          {getArtistLinks(song.artists)}
                        </div>
                      </div>
                      <div className="result-meta">
                        {song.bpm && isInstructorMode && (
                          <span className="meta-bpm">{song.bpm} BPM</span>
                        )}
                        <span className="meta-plays">{song.total_plays || 1} plays</span>
                      </div>
                    </a>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Artists Results */}
          {results.artists.length > 0 && (
            <div className="results-section">
              <h2>Artists ({results.artists.length})</h2>
              <div className="results-grid">
                {results.artists.map((artist) => (
                  <a
                    key={artist.id}
                    href={buildUrl(`/artist/${artist.id}`)}
                    className="result-card"
                  >
                    <div className="result-main">
                      <div className="result-title">{artist.name}</div>
                      <div className="result-subtitle">
                        {artist.total_songs} song{artist.total_songs !== 1 ? 's' : ''}
                      </div>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Playlists Results */}
          {results.playlists.length > 0 && (
            <div className="results-section">
              <h2>Playlists ({results.playlists.length})</h2>
              <div className="results-grid">
                {results.playlists.map((playlist) => (
                  <a
                    key={playlist.id}
                    href={buildUrl(`/playlist/${playlist.id}`)}
                    className="result-card"
                  >
                    <div className="result-main">
                      <div className="result-title">
                        {formatShortDate(playlist.playlist_date)}
                      </div>
                      <div className="result-subtitle">
                        {playlist.total_songs} songs
                      </div>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default Search;